import { type BrowserHistoryArgs } from 'instantsearch.js/es/lib/routers/history'
import { type UiState } from 'instantsearch.js/es/types/ui-state'
import { useMemo } from 'react'
import { type InstantSearchNextRouting } from 'react-instantsearch-nextjs'

import { useProductSearchContext } from '../context/productSearchContextValue'

import {
  productSearchAlgoliaIndexUiStateToRoute,
  type ProductSearchAlgoliaRouteState,
} from './productSearchAlgoliaIndexUiStateToRoute'
import { productSearchAlgoliaRouteToIndexUiState } from './productSearchAlgoliaRouteToIndexUiState'
import { productSearchOptimizeSearchQueryIfItHasPzns } from './productSearchProductIdValidationRules'

/**
 * Helper function to create a URL from the current location and route state.
 * Preserves the existing query parameters and adds the route state to it.
 */
const createUrl: BrowserHistoryArgs<ProductSearchAlgoliaRouteState>['createURL'] =
  ({ location, qsModule, routeState }) => {
    const url = new URL(location.toString())

    const parsedQs = qsModule.parse(location.search, {
      ignoreQueryPrefix: true,
    })

    const { pageNumber = 1, q: routeStateQuery } = routeState

    let query = routeStateQuery ?? parsedQs.q ?? parsedQs.query

    query =
      typeof query === 'string' && query !== ''
        ? productSearchOptimizeSearchQueryIfItHasPzns(query).searchQuery
        : undefined

    url.search = qsModule.stringify(
      {
        ...parsedQs,
        ...routeState,
        pageNumber: pageNumber <= 1 ? undefined : pageNumber,
        /* eslint-disable-next-line id-length -- set query under `q` as a Fock fallback, could be removed (switched to Algolia `query`) after Fock sundown */
        q: query,
      },
      {
        addQueryPrefix: true,
      },
    )

    return url.href
  }

export const useProductSearchAlgoliaRouting = (options?: {
  compositionID?: string
  routerIndex: string
}): InstantSearchNextRouting<UiState, ProductSearchAlgoliaRouteState> => {
  const {
    productSearchConfig: { baseAlgoliaConfig, sortByIndexes },
  } = useProductSearchContext()

  const routerIndex =
    options?.routerIndex ?? baseAlgoliaConfig.productsAlgoliaIndex

  const compositionID = options?.compositionID

  return useMemo(
    () => ({
      router: {
        cleanUrlOnDispose: false,
        createURL: createUrl,
      },

      stateMapping: {
        routeToState: (routeState): UiState => ({
          [compositionID ?? routerIndex]:
            productSearchAlgoliaRouteToIndexUiState(routeState, {
              sortByIndexes: sortByIndexes.map((item) => item.value),
            }),
        }),
        stateToRoute: (uiState): ProductSearchAlgoliaRouteState =>
          productSearchAlgoliaIndexUiStateToRoute(
            uiState[compositionID ?? routerIndex],
          ),
      },
    }),
    [compositionID, sortByIndexes, routerIndex],
  )
}
